import type {Thread} from '@github-ui/diff-lines/conversation-types'
import type {IssueComment} from '../../../page-data/payloads/issue-comments'
import type {ReviewSummary} from '../../../page-data/payloads/review-summaries'
import {assignThreadsToReviews, type ReviewWithThreads} from './assign-threads-to-reviews'

/**
 * A tagged wrapper that identifies an item in the merged conversation list
 * as either an issue comment or a review summary, preserving the original type
 * for rendering with the appropriate component.
 */
export type ConversationItem =
  | {type: 'issue-comment'; data: IssueComment; sortDate: string; key: string}
  | {type: 'review-summary'; data: ReviewWithThreads; sortDate: string; key: string}
  | {type: 'thread'; data: Thread; sortDate: string; key: string}

/**
 * Builds unsorted list of ConversationItems from issue comments and review
 * summaries for display in the Conversation tab.
 */
export function buildConversationItems(
  issueComments: readonly IssueComment[],
  reviewSummaries: readonly ReviewSummary[],
  threads: readonly Thread[],
): ConversationItem[] {
  const {reviews: reviewsWithThreads, unassignedThreads} = assignThreadsToReviews(reviewSummaries, threads)

  return [
    ...issueComments.map(
      (comment): ConversationItem => ({
        type: 'issue-comment',
        data: comment,
        sortDate: comment.createdAt,
        key: `issue-comment-${comment.id}`,
      }),
    ),
    ...reviewsWithThreads.map(
      (review): ConversationItem => ({
        type: 'review-summary',
        data: review,
        sortDate: review.summary.submittedAt,
        key: `review-summary-${review.summary.id}`,
      }),
    ),
    ...unassignedThreads.map(
      (thread): ConversationItem => ({
        type: 'thread',
        data: thread,
        // An empty thread shouldn't be possible, but we have to provide a default to satisfy the typechecker
        sortDate: thread.commentsData?.comments?.[0]?.createdAt ?? '0000-01-01T00:00:00Z',
        key: `thread-${thread.id}`,
      }),
    ),
  ]
}
